import { useId, useMemo } from 'react';
import { ratioColor } from '../../lib/map-utils';

/**
 * Dual-thumb range slider for the credentials-to-jobs ratio.
 *
 * 2026-07-02 (Georgetown feedback pass): reviewers said the binary
 * "Shortage / Surplus" chips forced a false choice — most exploration
 * happens INSIDE a category ("show me metros in the 0.4-0.8 shortage
 * band"), not across the 1.0 boundary. The slider preserves that
 * boundary as a visual mark (`--gold` inflection tick at 1.0) while
 * letting the user carve any [lo, hi] window they like.
 *
 * Implementation notes:
 *   - Two native <input type="range"> elements stacked on top of a
 *     shared track. Keeps keyboard + screen-reader semantics for free
 *     (arrow keys move each thumb; NVDA/JAWS announce min/max/now via
 *     the built-in ARIA on `role="slider"`).
 *   - `pointer-events` is disabled on the range inputs' tracks and
 *     re-enabled only on the thumbs so both thumbs remain draggable
 *     even when they overlap.
 *   - The min thumb clamps to `<= hi`; the max thumb clamps to
 *     `>= lo`. The zustand action does a second clamp so bad input
 *     from any caller can't wedge the state either.
 *   - Chrome, Firefox, and Safari each style range thumbs via a
 *     different pseudo-element; the shared styles live in
 *     `src/styles/range-slider.css` and are imported once at the app
 *     root so we don't ship the same rules per-component.
 *
 * 2026-07-08 (CR-14, Maria Slack 3a): "the blue circle can't reach
 * the very tip of the bar." Root cause -- native `<input type=range">`
 * thumbs are kept entirely INSIDE the input's own box by both Chrome
 * and Firefox (the thumb never overflows past the element's edges).
 * That means the thumb's CENTER only travels from `thumbRadius` to
 * `width - thumbRadius`, not the full `0` to `width`. The two range
 * inputs here are full-width (`left-0 w-full`), but the visual track
 * / dim-overlays / inflection tick were ALSO drawn full-width, so
 * they always had an ~8px (half the 16px thumb) gap between the
 * track's visual tip and the furthest the thumb could actually
 * reach. Fix: the visual scale (gradient track + dim overlays +
 * inflection tick) now renders inside an inner wrapper inset by
 * `THUMB_RADIUS_PX` (8px, matches the `16px` thumb in
 * `globals.css`) on each side via `left-2 right-2`. Percentage
 * positions computed against that narrower box line up exactly with
 * where the thumb's center can travel, so 0%/100% now visually
 * coincide with the true min/max thumb positions. The range inputs
 * themselves are untouched (still full-width hit targets).
 */
interface RatioRangeSliderProps {
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly value: readonly [number, number];
  readonly onChange: (next: readonly [number, number]) => void;
  /**
   * Accessible label for the whole control. Rendered as an invisible
   * `<span>` associated with both thumbs via `aria-labelledby`; visual
   * labels are supplied by the parent panel's uppercase heading, so
   * we don't repeat that string on screen.
   */
  readonly ariaLabel: string;
  /**
   * Optional numeric mark drawn as a tick on the track. Used on the map
   * panel to surface the 1.0 parity inflection so reviewers can see
   * where surplus starts without eyeballing the scale.
   */
  readonly inflection?: number;
}

/** Coerce any incoming value to the slider's [min, max] domain. */
function clamp(v: number, lo: number, hi: number): number {
  if (!Number.isFinite(v)) return lo;
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Render a value with the minimum decimals needed. `1.0` stays as
 * "1", `0.35` stays as "0.35". The map panel's readout wants concise
 * numbers, not `0.30 – 1.00`.
 */
function fmt(n: number): string {
  const rounded = Math.round(n * 100) / 100;
  return String(rounded);
}

/**
 * Build the CSS gradient stops for the track. Mirrors MapLegend's
 * `buildRatioGradient()` so the slider and the map legend read as
 * "same scale." Sampled at 11 points across the slider's [min, max]
 * domain via `ratioColor()`, which handles the rose->gold->green
 * anchors + clamping above 1.8.
 */
function buildTrackGradient(min: number, max: number): string {
  const stops: string[] = [];
  for (let i = 0; i <= 10; i++) {
    const t = i / 10;
    const ratio = min + t * (max - min);
    stops.push(`${ratioColor(ratio)} ${t * 100}%`);
  }
  return `linear-gradient(to right, ${stops.join(', ')})`;
}

export function RatioRangeSlider({
  min,
  max,
  step,
  value,
  onChange,
  ariaLabel,
  inflection,
}: RatioRangeSliderProps) {
  const labelId = useId();
  const [lo, hi] = value;
  // Guard against callers that hand us a value outside the domain
  // (e.g. tests, hot-module-reload with stale state).
  const safeLo = clamp(lo, min, max);
  const safeHi = clamp(hi, min, max);
  const span = max - min || 1;
  const leftPct = ((safeLo - min) / span) * 100;
  const rightPct = ((safeHi - min) / span) * 100;
  const inflectionPct =
    typeof inflection === 'number' && inflection >= min && inflection <= max
      ? ((inflection - min) / span) * 100
      : null;
  // Recompute only when the domain changes -- the gradient string is
  // otherwise static across renders.
  const trackGradient = useMemo(() => buildTrackGradient(min, max), [min, max]);

  return (
    <div className="w-full">
      <span id={labelId} className="sr-only">
        {ariaLabel}
      </span>

      {/* Track + fill + thumbs. Height is set on the wrapper (28px)
          so the thumbs (h-4 = 16px) have vertical room to render
          past the track (h-1 = 4px) without clipping. */}
      <div className="relative h-7 select-none">
        {/* Visual scale wrapper — inset `left-2 right-2` (8px each
            side = THUMB_RADIUS_PX, half the 16px thumb) so the 0%/100%
            positions computed below land exactly where the native
            thumb's center can actually travel (CR-14). Everything
            inside is positioned as a percentage of THIS box, not the
            full-width outer wrapper. */}
        <div aria-hidden="true" className="pointer-events-none absolute left-2 right-2 top-0 h-full">
          {/* Base track — rose→gold→green gradient sampled through
              `ratioColor()` so the slider matches the map legend's scale.
              2026-07-02 (Georgetown feedback): reviewers wanted the track
              itself to color-code the shortage / surplus regions so the
              slider communicates the scale, not just the selection. */}
          <div
            className="absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 rounded-full"
            style={{ background: trackGradient }}
          />
          {/* Outside-thumbs dim — semi-transparent white overlay on the
              regions the user has excluded, so the "active" band between
              the thumbs pops against the muted flanks. Two separate divs
              (left of min, right of max) so we don't have to fight
              `linear-gradient` masking. */}
          {leftPct > 0 && (
            <div
              className="absolute top-1/2 h-1 -translate-y-1/2 rounded-l-full bg-white/60"
              style={{ left: 0, width: `${leftPct}%` }}
            />
          )}
          {rightPct < 100 && (
            <div
              className="absolute top-1/2 h-1 -translate-y-1/2 rounded-r-full bg-white/60"
              style={{ left: `${rightPct}%`, right: 0 }}
            />
          )}
          {/* Optional inflection tick (e.g. 1.0 = parity). Navy anchor
              keeps the mark legible against the mid-gradient warm tones
              (gold at ~50% would blend into the track). Sits ABOVE the
              dim overlays so it stays visible in the excluded regions. */}
          {inflectionPct !== null && (
            <div
              className="absolute top-1/2 h-3 w-[2px] -translate-x-1/2 -translate-y-1/2 rounded-sm bg-navy/70"
              style={{ left: `${inflectionPct}%` }}
            />
          )}
        </div>

        {/* Min thumb — clamped to `<= hi`. */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={safeLo}
          onChange={(e) => {
            const nextLo = Math.min(clamp(Number(e.target.value), min, max), safeHi);
            onChange([nextLo, safeHi]);
          }}
          aria-labelledby={labelId}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={safeLo}
          aria-valuetext={`Minimum ratio ${fmt(safeLo)}`}
          className="ratio-slider-input pointer-events-none absolute left-0 top-0 h-7 w-full bg-transparent"
        />
        {/* Max thumb — clamped to `>= lo`. */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={safeHi}
          onChange={(e) => {
            const nextHi = Math.max(clamp(Number(e.target.value), min, max), safeLo);
            onChange([safeLo, nextHi]);
          }}
          aria-labelledby={labelId}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={safeHi}
          aria-valuetext={`Maximum ratio ${fmt(safeHi)}`}
          className="ratio-slider-input pointer-events-none absolute left-0 top-0 h-7 w-full bg-transparent"
        />
      </div>

      {/* Numeric readout — reflects the exact numbers the user set.
          Aria-live off (aria-valuetext on each thumb already
          announces changes to screen readers). */}
      <div className="mt-1 flex items-center justify-between text-[11px] font-semibold text-text-3">
        <span>{fmt(safeLo)}</span>
        <span className="text-[10px] font-normal text-text-4" aria-hidden="true">
          to
        </span>
        <span>{fmt(safeHi)}</span>
      </div>
    </div>
  );
}
